/**
 * Budget —— 預算 vs 實際（P2-3；財務規劃）
 * 可以逐行改預算金額 → 一次過儲存（每行留審計）。實際數字由分錄計（同月報一致）。
 */
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import TopBar from '../components/TopBar';
import { api, currentPeriod, fmtMoney, type BudgetReport, type Meta } from '../lib/api';

/** 輸入「元」→ 整數分（唔准浮點；最多 2 位小數）*/
export function toCentsInput(v: string): number | null {
  const s = v.trim().replace(/,/g, '');
  if (!s) return 0;
  const m = /^(\d*)(?:\.(\d{1,2}))?$/.exec(s);
  if (!m) return null;
  return Number(m[1] || '0') * 100 + Number((m[2] ?? '').padEnd(2, '0') || '0');
}
const toYuan = (cents: number) => (cents / 100).toFixed(2);

export default function Budget({ meta }: { meta: Meta }) {
  const { t } = useTranslation();
  const [entityId, setEntityId] = useState(meta.entities[0]?.id ?? '');
  const [period, setPeriod] = useState(currentPeriod());
  const [r, setR] = useState<BudgetReport | null>(null);
  const [edits, setEdits] = useState<Record<string, string>>({});
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState<number | null>(null);

  const load = useCallback(() => {
    setErr(null);
    api.budgets(entityId, period)
      .then((x) => { setR(x); setEdits({}); setSaved(null); })
      .catch((e: unknown) => setErr(e instanceof Error ? e.message : String(e)));
  }, [entityId, period]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    const lines: { glCode: string; amountCents: number }[] = [];
    for (const [glCode, value] of Object.entries(edits)) {
      const cents = toCentsInput(value);
      if (cents === null) { setErr(t('budget.bad_amount', { v: value })); return; }
      lines.push({ glCode, amountCents: cents });
    }
    if (!lines.length) { setErr(t('budget.no_changes')); return; }
    setBusy(true); setErr(null);
    try {
      const res = await api.setBudgets(entityId, period, lines);
      setSaved(res.updated);
      load();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally { setBusy(false); }
  };

  return (
    <main className="page">
      <TopBar title={t('budget.heading')} />

      <section className="card">
        <div className="fields">
          <label className="field">
            <span>{t('expense.entity')}</span>
            <select value={entityId} onChange={(e) => setEntityId(e.target.value)}>
              {meta.entities.map((e) => <option key={e.id} value={e.id}>{e.name_zh}</option>)}
            </select>
          </label>
          <label className="field">
            <span>{t('list.month')}</span>
            <input type="month" value={period} onChange={(e) => setPeriod(e.target.value)} />
          </label>
        </div>
        {r && (
          <div className="totals">
            <div><small>{t('budget.expense_budget')}</small><b>{fmtMoney(r.totals.expenseBudget)}</b></div>
            <div><small>{t('budget.expense_actual')}</small><b className="neg">{fmtMoney(r.totals.expenseActual)}</b></div>
            <div><small>{t('budget.income_budget')}</small><b>{fmtMoney(r.totals.incomeBudget)}</b></div>
            <div><small>{t('budget.income_actual')}</small><b className="pos">{fmtMoney(r.totals.incomeActual)}</b></div>
          </div>
        )}
        {err && <p className="err">{err}</p>}
        {saved !== null && <p className="ok">{t('budget.saved', { n: saved })}</p>}
        {r?.warnings.map((w) => <p key={w} className="note">⚠️ {w}</p>)}
      </section>

      {r && (
        <section className="card">
          <table className="ledger">
            <thead>
              <tr>
                <th>{t('detail.gl')}</th>
                <th className="num">{t('budget.budget')}</th>
                <th className="num">{t('budget.actual')}</th>
                <th className="num">{t('budget.variance')}</th>
                <th className="num">%</th>
              </tr>
            </thead>
            <tbody>
              {r.rows.map((row) => {
                const value = edits[row.glCode] ?? (row.budgetCents ? toYuan(row.budgetCents) : '');
                const over = row.type === 'expense' ? row.varianceCents < 0 : row.varianceCents > 0;
                return (
                  <tr key={row.glCode} className={row.unbudgeted ? 'row-warn' : ''}>
                    <td>{row.glCode} {row.nameZh}{row.unbudgeted ? ` ⚠️ ${t('budget.unbudgeted')}` : ''}</td>
                    <td className="num">
                      <input
                        className="budget-input" inputMode="decimal" value={value}
                        onChange={(e) => setEdits((s) => ({ ...s, [row.glCode]: e.target.value }))}
                        placeholder="0.00"
                      />
                    </td>
                    <td className="num">{fmtMoney(row.actualCents)}</td>
                    <td className={`num ${row.varianceCents >= 0 ? 'pos' : 'neg'}`}>{fmtMoney(row.varianceCents)}</td>
                    <td className={`num ${over ? 'neg' : ''}`}>{row.usedPct === null ? '—' : `${row.usedPct}%`}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <button className="cta" onClick={() => void save()} disabled={busy}>{t('budget.save')}</button>
          <p className="note">{t('budget.note')}</p>
        </section>
      )}
    </main>
  );
}
